import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { fetchDutySummary, type DutySummary } from '@/features/duties/api/api'
import { dutySummaryKeys } from '@/lib/queryKeys'

export type { DutySummary }

/**
 * Fetches the duty summary (name, counts, computer_use flag, status) for a single duty.
 *
 * Accepts optional `placeholderData` so navigation from the duties list renders
 * instantly while the authoritative fetch completes in the background. Missing
 * `status` in the placeholder defaults to `'inactive'` (conservative — we'd rather
 * show a pending duty as inactive than claim it's running).
 */
export function useDutySummary(
  dutyId: string | undefined,
  placeholder?: Partial<DutySummary>
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: dutySummaryKeys.detail(organization?.id, dutyId),
    enabled: !!organization?.id && !!dutyId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: placeholder
      ? {
          id: dutyId ?? '',
          name: placeholder.name ?? null,
          computer_use: placeholder.computer_use ?? false,
          status: placeholder.status ?? 'inactive',
          patient_count: placeholder.patient_count ?? 0,
          assignment_population_count:
            placeholder.assignment_population_count ?? null,
          assignment_completed_count:
            placeholder.assignment_completed_count ?? null,
          call_count: placeholder.call_count ?? 0,
          computer_use_workflow_count:
            placeholder.computer_use_workflow_count ?? 0,
          computer_use_session_count:
            placeholder.computer_use_session_count ?? 0,
          computer_use_workflow_slugs:
            placeholder.computer_use_workflow_slugs ?? [],
          config_updated_at: placeholder.config_updated_at ?? null,
          workflow_graph: placeholder.workflow_graph ?? null,
          is_texting_only: placeholder.is_texting_only ?? false,
          health_check_enabled: placeholder.health_check_enabled ?? false,
          access: placeholder.access ?? [],
        }
      : undefined,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      if (!dutyId) throw new Error('Missing duty id')
      return fetchDutySummary(token, dutyId, signal)
    },
  })
}
